import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, DataTable, StatusBadge, ColumnSettingsButton, btnPrimary, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'
import { alertTypeLabel, relativeTime } from './fleet-helpers'
import type { FleetAlert, AlertThresholds, UserRole, FleetAlertSummary } from '@alpha-edge/shared'
import { useTableColumns } from '../../hooks/use-table-columns'

type AlertFilter = 'active' | 'acknowledged' | 'resolved' | ''

export function FleetAlertsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const role = user?.role as UserRole | undefined
  const isMedflow = role === 'medflow_admin'

  const [alerts, setAlerts] = useState<readonly FleetAlert[]>([])
  const [summary, setSummary] = useState<FleetAlertSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<AlertFilter>('active')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [refreshKey, setRefreshKey] = useState(0)
  const { tableId: _, ...tableColumnProps } = useTableColumns('fleet-alerts')

  const [thresholds, setThresholds] = useState<AlertThresholds | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    const params = statusFilter ? `?status=${statusFilter}` : ''
    Promise.all([
      api.get<readonly FleetAlert[]>(`/fleet/alerts${params}`),
      api.get<FleetAlertSummary>('/fleet/alerts/summary'),
    ])
      .then(([alertRes, summaryRes]) => {
        if (cancelled) return
        setAlerts(alertRes.data ?? [])
        setSummary(summaryRes.data ?? null)
        setSelected(new Set())
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load alerts')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [statusFilter, refreshKey])

  useEffect(() => {
    if (!isMedflow) return
    let cancelled = false
    api
      .get<AlertThresholds>('/fleet/alerts/thresholds')
      .then((res) => {
        if (!cancelled) setThresholds(res.data ?? null)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isMedflow])

  const handleAcknowledge = () => {
    if (selected.size === 0) return
    api
      .post<{ acknowledged: number }>('/fleet/alerts/acknowledge', {
        alertIds: [...selected],
      })
      .then(() => setRefreshKey((k) => k + 1))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to acknowledge'))
  }

  const handleSaveThresholds = () => {
    if (!thresholds) return
    setSaving(true)
    api
      .put<AlertThresholds>('/fleet/alerts/thresholds', thresholds)
      .then((res) => {
        setThresholds(res.data ?? thresholds)
        setSaving(false)
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to save thresholds')
        setSaving(false)
      })
  }

  const detailPath = isMedflow ? '/fleet' : '/licenses'

  const columns: readonly Column<FleetAlert>[] = [
    {
      key: 'severity',
      header: 'Severity',
      render: (r) => <StatusBadge status={r.severity} />,
    },
    ...(isMedflow
      ? [
          {
            key: 'customer' as const,
            header: 'Customer',
            render: (r: FleetAlert) => r.ownerOrganizationName ?? '—',
          },
        ]
      : []),
    {
      key: 'environmentName',
      header: 'Environment',
      render: (r) => <strong>{r.environmentName}</strong>,
      sortable: true,
    },
    {
      key: 'alertType',
      header: 'Type',
      render: (r) => alertTypeLabel(r.alertType),
    },
    {
      key: 'message',
      header: 'Message',
      render: (r) => r.message,
    },
    {
      key: 'firedAt',
      header: 'Fired',
      render: (r) => relativeTime(r.firedAt),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.status} />,
    },
  ]

  const filterStyle = (f: AlertFilter): React.CSSProperties => ({
    padding: '0.35rem 0.75rem',
    border: `1px solid ${statusFilter === f ? colors.accent : colors.border}`,
    borderRadius: '4px',
    background: statusFilter === f ? '#eef4ff' : '#fff',
    color: statusFilter === f ? colors.accent : colors.text,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
  })

  const inputStyle: React.CSSProperties = {
    width: '80px',
    padding: '0.3rem 0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '13px',
  }

  return (
    <div>
      <PageHeader
        title="Alerts"
        subtitle={summary ? `${summary.activeCount} active (${summary.criticalCount} critical)` : ''}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            {selected.size > 0 && statusFilter === 'active' && (
              <button type="button" onClick={handleAcknowledge} style={btnPrimary}>
                Acknowledge ({selected.size})
              </button>
            )}
            {isMedflow && (
              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                style={{
                  ...btnPrimary,
                  background: showSettings ? colors.accent : '#fff',
                  color: showSettings ? '#fff' : colors.accent,
                  border: `1px solid ${colors.accent}`,
                }}
              >
                Settings
              </button>
            )}
          </div>
        }
      />

      {isMedflow && showSettings && thresholds && (
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1rem',
            marginBottom: '1rem',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '0.75rem', color: colors.textHeading }}>
            Alert Thresholds
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', color: colors.text }}>
              Stale after{' '}
              <input
                type="number"
                min={1}
                max={1440}
                value={thresholds.staleDeploymentMinutes}
                onChange={(e) =>
                  setThresholds({ ...thresholds, staleDeploymentMinutes: parseInt(e.target.value, 10) || 10 })
                }
                style={inputStyle}
              />{' '}
              min
            </label>
            <label style={{ fontSize: '12px', color: colors.text }}>
              License warning at{' '}
              <input
                type="number"
                min={1}
                max={365}
                value={thresholds.licenseExpiryWarningDays}
                onChange={(e) =>
                  setThresholds({
                    ...thresholds,
                    licenseExpiryWarningDays: parseInt(e.target.value, 10) || 30,
                  })
                }
                style={inputStyle}
              />{' '}
              days
            </label>
            <label style={{ fontSize: '12px', color: colors.text }}>
              License critical at{' '}
              <input
                type="number"
                min={1}
                max={90}
                value={thresholds.licenseExpiryCriticalDays}
                onChange={(e) =>
                  setThresholds({
                    ...thresholds,
                    licenseExpiryCriticalDays: parseInt(e.target.value, 10) || 7,
                  })
                }
                style={inputStyle}
              />{' '}
              days
            </label>
            <button type="button" onClick={handleSaveThresholds} disabled={saving} style={btnPrimary}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {(['active', 'acknowledged', 'resolved', ''] as const).map((f) => (
          <button key={f || 'all'} type="button" onClick={() => setStatusFilter(f)} style={filterStyle(f)}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={alerts}
          keyField="id"
          selectable={statusFilter === 'active'}
          selectedKeys={selected}
          onSelectionChange={setSelected}
          onRowClick={(r) => navigate(`${detailPath}/${r.environmentId}`)}
          emptyMessage="No alerts match the current filter."
          {...tableColumnProps}
        />
      )}
    </div>
  )
}
